export const STORAGE_KEY = 'nooni:email-offer:return:v1';
export const TTL_MS = 2 * 60 * 60 * 1000;
export const AUTO_APPLY_ENABLED = false;
export function safeProductPath(raw, origin) {
  if (typeof raw !== 'string' || raw.length > 8192 || !raw.startsWith('/') || raw.startsWith('//') || /[\\\u0000-\u001f\u007f]/.test(raw)) return null;
  const path = raw.split(/[?#]/)[0];
  if (/%(?:2f|5c|2e|25)/i.test(path) || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*\/?$/i.test(path)) return null;
  try {
    const url = new URL(raw, origin);
    return url.origin === origin && !url.username && !url.password ? url.pathname + url.search + url.hash : null;
  } catch { return null; }
}
export function validRecord(record, origin, themeId, now = Date.now()) {
  if (!record || record.v !== 1 || record.origin !== origin || record.themeId !== String(themeId) ||
      !Number.isFinite(record.at) || record.at > now + 1000 || now - record.at > TTL_MS ||
      !/^\d+$/.test(record.productId) || !/^\d+$/.test(record.variant) ||
      !Number.isInteger(record.quantity) || record.quantity < 1 || record.quantity > 9999 ||
      !['captured', 'returning', 'navigated'].includes(record.phase)) return null;
  const path = safeProductPath(record.path, origin);
  if (!path) return null;
  const url = new URL(path, origin);
  if (url.searchParams.get('preview_theme_id') !== String(themeId) || url.searchParams.get('variant') !== record.variant) return null;
  return { ...record, path };
}
export function allowedQuantity(value, input) {
  const min = Math.max(1, Number(input.min) || 1);
  const max = input.max ? Number(input.max) : 9999;
  const step = Number(input.step) > 0 ? Number(input.step) : 1;
  return Number.isInteger(value) && value >= min && value <= max && (value - min) % step === 0;
}
function preferencesAllowed() {
  try { return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true; } catch { return false; }
}
function readRecord(themeId) {
  if (!preferencesAllowed()) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record = validRecord(JSON.parse(raw), location.origin, themeId);
    if (!record) sessionStorage.removeItem(STORAGE_KEY);
    return record;
  } catch { return null; }
}
function writeRecord(record) {
  if (!preferencesAllowed()) return false;
  try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(record)); return true; } catch { return false; }
}
function forgetRecord() { try { sessionStorage.removeItem(STORAGE_KEY); } catch {  } }
export function routeEmailReturn({ themeId, unavailable }) {
  if (new URL(location.href).searchParams.get('nooni_email_return') !== '1') return;
  const record = readRecord(themeId);
  if (record && record.phase === 'captured') {
    if (writeRecord({ ...record, phase: 'returning' })) {
      location.replace(record.path);
      return;
    }
  }
  const message = document.createElement('p');
  message.className = 'page-width text-center';
  message.setAttribute('role', 'status');
  message.textContent = unavailable;
  document.getElementById('main')?.prepend(message);
}
if (typeof HTMLElement !== 'undefined' && !customElements.get('nooni-email-offer')) {
  customElements.define('nooni-email-offer', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.link = this.querySelector('a');
      this.status = this.querySelector('[data-email-offer-status]');
      this.link?.addEventListener('click', event => this.capture(event), { signal });
      window.addEventListener('pageshow', () => this.restore(), { signal });
      document.addEventListener('visitorConsentCollected', () => {
        if (!preferencesAllowed()) forgetRecord();
      }, { signal });
      if (!preferencesAllowed()) this.status.textContent = this.dataset.fallback;
      this.restore();
    }
    disconnectedCallback() { this.controller?.abort(); }
    get form() { return document.getElementById(this.dataset.formId); }
    variants() {
      try { return JSON.parse(this.dataset.variants).map(String); } catch { return []; }
    }
    capture(event) {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
      const form = this.form;
      const variant = String(form?.elements.namedItem('id')?.value || '');
      const quantity = Number(form?.elements.namedItem('quantity')?.value || 1);
      if (!this.variants().includes(variant) || !Number.isInteger(quantity) || quantity < 1 || quantity > 9999) return;
      const url = new URL(location.href);
      url.searchParams.set('variant', variant);
      url.searchParams.set('preview_theme_id', this.dataset.previewThemeId);
      const path = safeProductPath(url.pathname + url.search + url.hash, location.origin);
      if (!path) return;
      const saved = writeRecord({
        v: 1, origin: location.origin, themeId: this.dataset.previewThemeId,
        productId: this.dataset.productId, variant, quantity, path,
        at: Date.now(), phase: 'captured'
      });
      if (!saved) this.status.textContent = this.dataset.fallback;
    }
    restore() {
      const record = readRecord(this.dataset.previewThemeId);
      if (!record || record.productId !== this.dataset.productId || !this.variants().includes(record.variant)) return;
      const form = this.form;
      const selected = String(form?.elements.namedItem('id')?.value || '');
      if (selected !== record.variant) {
        if (record.phase === 'navigated') { forgetRecord(); return; }
        if (writeRecord({ ...record, phase: 'navigated' })) location.replace(record.path);
        return;
      }
      const input = form?.elements.namedItem('quantity');
      const quantityValid = !input ? record.quantity === 1 : allowedQuantity(record.quantity, input);
      if (input && quantityValid) {
        input.value = String(record.quantity);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      forgetRecord();
      this.status.textContent = quantityValid ? this.dataset.restored : this.dataset.quantityChanged;
    }
  });
}
